<template>
  <div class="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50">
    <header
      id="header"
      class="relative overflow-hidden bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800"
    >
      <div class="opacity-50 bg-black absolute top-0 left-0 w-full h-full"></div>
      <div class="absolute inset-0 bg-black/10"></div>
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div class="text-center lg:pt-[250px] pt-[300px]">
          <h1 class="text-5xl font-bold text-white mb-8">
            <!--            <span class="block">Linguidia</span>-->
            <span class="block text-2xl sm:text-2xl font-medium mt-4">
              Maîtrisez les langues avec confiance
            </span>
          </h1>
          <p class="mb-10 text-white max-w-xl mx-auto leading-relaxed">
            Préparez-vous aux examens officiels avec nos exercices adaptatifs, suivez vos progrès en
            temps réel et atteignez l'excellence linguistique.
          </p>
          <div class="flex flex-col sm:flex-row gap-6 justify-center">
            <router-link
              to="/dashboard/exercises"
              class="inline-flex items-center justify-center px-10 py-5 bg-white text-primary-600 font-bold rounded-xl hover:bg-primary-50 hover:scale-105 transition-all duration-300 shadow-2xl"
            >
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Commencer maintenant
            </router-link>
            <router-link
              v-if="!isAuthenticated"
              to="/login"
              class="inline-flex items-center px-10 py-5 justify-center border-3 border-white text-white font-bold rounded-xl hover:bg-white hover:text-primary-600 hover:scale-105 transition-all duration-300"
            >
              Se connecter
            </router-link>
          </div>
        </div>
      </div>
      <div class="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
      <div
        class="absolute bottom-10 right-10 w-16 h-16 bg-white/10 rounded-full animate-bounce"
      ></div>
      <div class="absolute top-1/2 left-1/4 w-12 h-12 bg-white/5 rounded-full animate-ping"></div>
    </header>

    <section class="py-20 sm:py-28">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-20">
          <h2 class="text-4xl sm:text-4xl font-bold text-gray-900 mb-6">
            Pourquoi choisir Linguidia ?
          </h2>
          <p class="text-gray-600 max-w-4xl mx-auto">
            Une approche moderne pour exceller dans l'apprentissage des langues
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          <div
            class="bg-white rounded-2xl shadow-xl p-10 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-primary-100"
          >
            <div
              class="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-8"
            >
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">Préparation Certifiante</h3>
            <p class="text-gray-600 leading-relaxed">
              Préparez tous les examens officiels avec des simulations réalistes et des corrections
              détaillées.
            </p>
          </div>

          <div
            class="bg-white rounded-2xl shadow-xl p-10 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-primary-100"
          >
            <div
              class="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-8"
            >
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">Analytics Avancés</h3>
            <p class="text-gray-600 leading-relaxed">
              Visualisez vos performances avec des graphiques détaillés et identifiez précisément
              vos points d'amélioration.
            </p>
          </div>

          <div
            class="bg-white rounded-2xl shadow-xl p-10 hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-primary-100"
          >
            <div
              class="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mb-8"
            >
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">IA Adaptative</h3>
            <p class="text-gray-600 leading-relaxed">
              Notre intelligence artificielle s'adapte à votre niveau et personnalise chaque
              exercice pour maximiser votre progression.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      style="
        background-image: url('/students1.webp');
        background-position: center;
        background-repeat: no-repeat;
      "
      class="py-20 relative bg-gradient-to-br from-primary-50 to-primary-100"
    >
      <div class="absolute top-0 left-0 w-full h-full bg-black opacity-60 z-[1]"></div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-9 relative">
        <div class="text-center mb-16">
          <h2 class="text-4xl sm:text-3xl font-bold text-white mb-6">Examens disponibles</h2>
          <p class="text-white max-w-4xl mx-auto">
            Préparez-vous à tous les examens officiels avec nos modules spécialisés
          </p>
        </div>

        <div v-if="languageStore.loading" class="flex justify-center">
          <LoadingState></LoadingState>
        </div>

        <div v-else-if="examTypes.length > 0" class="flex justify-center">
          <div class="">
            <div
              v-for="examType in examTypes"
              :key="examType.id"
              class="bg-white rounded-2xl shadow-xl p-8 text-center hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer border border-primary-100"
              @click="navigateToExamType(examType.slug)"
            >
              <div
                class="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
              >
                <span class="text-white font-bold text-2xl">{{
                  examType.name?.charAt(0) || '?'
                }}</span>
              </div>
              <h3 class="font-bold text-xl text-gray-900 mb-3">{{ examType.name }}</h3>
              <p class="text-gray-600 leading-relaxed">
                {{
                  examType.description || 'Préparez-vous efficacement avec nos modules spécialisés'
                }}
              </p>
              <div class="mt-6 inline-flex items-center text-primary-600 font-semibold">
                Découvrir
                <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-center text-gray-500 text-xl">
          <p>Aucun type d'examen disponible pour le moment.</p>
        </div>
      </div>
    </section>

    <section class="py-20 sm:py-28">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl sm:text-3xl font-bold text-gray-900 mb-6">
            Ils ont réussi avec Linguidia
          </h2>
          <p class="text-gray-600 max-w-4xl mx-auto">
            Découvrez les témoignages de nos utilisateurs qui ont transformé leur maîtrise des
            langues
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          <div class="bg-white rounded-2xl shadow-xl p-10 border border-primary-100 flex flex-col">
            <div class="flex items-center mb-6">
              <img
                loading="lazy"
                class="w-16 h-16 rounded-full mr-4 object-cover bg-primary-200"
                src="@/assets/img/male.webp"
                alt="Avatar Jean-Pierre Tremblay"
              />
              <div>
                <h3 class="font-bold text-lg text-gray-900">Jean-Pierre Takam</h3>
                <p class="text-primary-600 font-semibold">TCF Canada - NCLC 8 (C1)</p>
              </div>
            </div>
            <div class="text-yellow-400 mb-4 text-xl">★★★★★</div>
            <p class="text-gray-600 text-lg leading-relaxed italic grow">
              "Linguidia a été un atout majeur dans ma préparation pour le TCF Canada. Les
              simulations d'examen étaient incroyablement réalistes et l'IA m'a aidé à corriger mes
              erreurs en expression orale et écrite."
            </p>
          </div>

          <div class="bg-white rounded-2xl shadow-xl p-10 border border-primary-100 flex flex-col">
            <div class="flex items-center mb-6">
              <img
                loading="lazy"
                class="w-16 h-16 rounded-full mr-4 object-cover bg-primary-200"
                src="@/assets/img/female.webp"
                alt="Avatar Fatima Diallo"
              />
              <div>
                <h3 class="font-bold text-lg text-gray-900">Fatima Sali</h3>
                <p class="text-primary-600 font-semibold">TCF Canada - NCLC 7</p>
              </div>
            </div>
            <div class="text-yellow-400 mb-4 text-xl">★★★★★</div>
            <p class="text-gray-600 text-lg leading-relaxed italic grow">
              "La flexibilité de Linguidia m'a permis de me préparer au TEF Canada tout en
              travaillant. Le suivi de progression est très motivant et les leçons personnalisées
              m'ont aidée à obtenir le niveau requis."
            </p>
          </div>

          <div class="bg-white rounded-2xl shadow-xl p-10 border border-primary-100 flex flex-col">
            <div class="flex items-center mb-6">
              <img
                loading="lazy"
                class="w-16 h-16 rounded-full mr-4 object-cover bg-primary-200"
                src="@/assets/img/male.webp"
                alt="Avatar Youssef Amrani"
              />
              <div>
                <h3 class="font-bold text-lg text-gray-900">Youssef Amrani</h3>
                <p class="text-primary-600 font-semibold">TCF Canada - NCLC 9 (C2)</p>
              </div>
            </div>
            <div class="text-yellow-400 mb-4 text-xl">★★★★★</div>
            <p class="text-gray-600 text-lg leading-relaxed italic grow">
              "Je n'étais pas à l'aise avec l'épreuve d'expression orale. Les entraînements avec
              l'IA m'ont donné la confiance nécessaire pour m'exprimer clairement. Résultat : NCLC 9
              ! Je recommande à 100%."
            </p>
          </div>
        </div>
      </div>
    </section>
    <div
      v-if="generalStore.packages && generalStore.packages.length"
      class="py-[100px]"
      style="background: url('/login-bg.webp'); background-repeat: no-repeat"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl sm:text-3xl font-bold text-gray-900 mb-6">Packages</h2>
          <p class="text-gray-600 max-w-4xl mx-auto">
            Choisissez le plan qui correspond à vos objectifs d'apprentissage
          </p>
        </div>
      </div>

      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 px-4 max-w-7xl sm:px-6 lg:px-8 gap-8 mx-auto"
      >
        <div
          v-for="(pkg, index) in generalStore.packages"
          :key="pkg.slug"
          :class="`package-card transform transition-all duration-500 hover:scale-105 hover:-translate-y-4`"
          :style="`animation-delay: ${index * 100}ms`"
        >
          <div
            class="relative h-full bg-white rounded-3xl border border-gray-200 shadow-xl overflow-hidden group hover:shadow-2xl hover:border-purple-200 transition-all duration-500"
          >
            <div
              v-if="pkg.promo_code && pkg.reduction_percentage > 0"
              class="absolute top-6 right-6 z-20"
            >
              <div class="relative">
                <div
                  class="absolute inset-0 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full blur animate-pulse"
                ></div>
                <div
                  class="relative bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg transform rotate-12 hover:rotate-0 transition-transform duration-300"
                >
                  -{{ pkg.reduction_percentage }}%
                </div>
              </div>
            </div>

            <div
              class="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-transparent to-primary-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            ></div>

            <div class="relative py-4 text-center bg-gray-50 border-b border-gray-200">
              <h3 class="text-2xl font-bold text-gray-900 mb-4 uppercase tracking-wide">
                {{ pkg.name }}
              </h3>

              <div class="">
                <div
                  v-if="pkg.promo_code && pkg.reduction_percentage > 0"
                  class="text-gray-400 text-lg line-through mb-1"
                >
                  {{ formatPrice(pkg.price) }} XAF
                </div>
                <div
                  class="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-300 to-primary-600"
                >
                  {{ formatPrice(pkg.promo_price || pkg.price) }}
                </div>
                <div class="text-gray-600 text-lg font-medium">
                  XAF / {{ pkg.period }} {{ pkg.period > 1 ? 'mois' : 'mois' }}
                </div>
              </div>
            </div>

            <div class="px-8 pb-8">
              <div class="space-y-4 mb-8 pt-4">
                <div
                  class="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100"
                >
                  <div class="flex items-center">
                    <svg
                      class="w-5 h-5 text-purple-600 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      ></path>
                    </svg>
                    <span class="text-gray-700 font-medium">Type d'examen</span>
                  </div>
                  <span
                    class="font-bold text-gray-900 bg-gradient-to-r from-purple-50 to-blue-50 px-3 py-1 rounded-lg border border-purple-100"
                    >{{ pkg.exam_type }}</span
                  >
                </div>

                <div
                  class="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100"
                >
                  <div class="flex items-center">
                    <svg
                      class="w-5 h-5 text-primary-600 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    <span class="text-gray-700 font-medium">Durée d'accès</span>
                  </div>
                  <span class="font-bold text-gray-900">{{ pkg.period }} mois</span>
                </div>

                <div
                  v-if="pkg.promo_code && pkg?.reduction_percentage > 0"
                  class="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200"
                >
                  <div class="flex items-center">
                    <svg
                      class="w-5 h-5 text-green-600 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      ></path>
                    </svg>
                    <span class="text-green-700 font-medium">Code promo</span>
                  </div>
                  <span class="font-bold text-green-700 bg-green-100 px-3 py-1 rounded-lg">
                    {{ pkg.promo_code }}
                  </span>
                </div>

                <div
                  v-if="pkg.promo_code && pkg.reduction_percentage > 0"
                  class="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200"
                >
                  <div class="flex items-center justify-between">
                    <span class="text-green-700 font-medium flex items-center">
                      <svg
                        class="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                        ></path>
                      </svg>
                      Vous économisez
                    </span>
                    <span class="font-bold text-green-700">
                      {{ formatPrice(pkg.price - (pkg.promo_price || pkg.price)) }} XAF
                    </span>
                  </div>
                </div>
              </div>

              <button
                @click="selectPackage(pkg)"
                name="Acheter le package"
                class="w-full group cursor-pointer relative overflow-hidden bg-gradient-to-r from-primary-300 to-primary-600 hover:from-primary-700 hover:to-blue-700 text-white font-bold py-5 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
                ></div>
                <span class="relative flex items-center justify-center">
                  <svg
                    class="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    ></path>
                  </svg>
                  Choisir {{ pkg.name }}
                </span>
              </button>

              <div class="mt-6 space-y-2">
                <div class="flex items-center text-gray-500 text-sm">
                  <svg
                    class="w-4 h-4 text-green-500 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  Accès immédiat après paiement
                </div>
                <div class="flex items-center text-gray-500 text-sm">
                  <svg
                    class="w-4 h-4 text-green-500 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  Support technique 24/7
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <LoadingState v-if="generalStore.isLoading"></LoadingState>

    <section class="py-20 sm:py-28">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl sm:text-3xl font-bold text-gray-900 mb-6">Comment ça marche ?</h2>
          <p class="text-gray-600 max-w-4xl mx-auto">
            Atteignez vos objectifs linguistiques en 3 étapes simples.
          </p>
        </div>

        <div class="relative">
          <div
            class="hidden md:block absolute top-1/2 -translate-y-1/2 left-0 w-full h-1 bg-primary-200"
          ></div>

          <div class="relative grid md:grid-cols-3 gap-16">
            <div class="text-center bg-white p-8 rounded-2xl shadow-lg border border-primary-100">
              <div
                class="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md border-2 border-white"
              >
                <span class="text-4xl font-bold text-primary-600">1</span>
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-4">Choisissez votre examen</h3>
              <p class="text-gray-600">
                Sélectionnez l'examen que vous souhaitez préparer parmi notre large catalogue
                (TOEFL, DELF, DELE, etc.).
              </p>
            </div>
            <div class="text-center bg-white p-8 rounded-2xl shadow-lg border border-primary-100">
              <div
                class="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md border-2 border-white"
              >
                <span class="text-4xl font-bold text-primary-600">2</span>
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-4">Entraînez-vous avec l'IA</h3>
              <p class="text-gray-600">
                Réalisez des exercices adaptatifs qui ciblent vos faiblesses et renforcent vos
                compétences jour après jour.
              </p>
            </div>
            <div class="text-center bg-white p-8 rounded-2xl shadow-lg border border-primary-100">
              <div
                class="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md border-2 border-white"
              >
                <span class="text-4xl font-bold text-primary-600">3</span>
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-4">Réussissez avec brio !</h3>
              <p class="text-gray-600">
                Passez votre examen en toute confiance grâce à une préparation complète et un suivi
                de vos progrès.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-20 sm:py-28 bg-gradient-to-br from-primary-50 to-primary-100">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl sm:text-3xl font-bold text-gray-900 mb-6">Questions fréquentes</h2>
          <p class="text-gray-600">Trouvez les réponses à vos interrogations les plus courantes.</p>
        </div>

        <div class="space-y-6">
          <details
            class="group bg-white p-6 rounded-xl shadow-lg border border-primary-200 cursor-pointer"
          >
            <summary class="flex items-center justify-between font-bold text-xl text-gray-900">
              Les examens de pratique sont-ils conformes aux vrais tests ?
              <svg
                class="w-6 h-6 transform transition-transform duration-300 group-open:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </summary>
            <p class="mt-4 text-gray-700 text-lg leading-relaxed">
              Absolument. Nos examens de pratique sont conçus par des experts linguistiques et
              suivent scrupuleusement le format, le type de questions et le niveau de difficulté des
              examens officiels pour vous mettre dans des conditions réelles.
            </p>
          </details>
          <details
            class="group bg-white p-6 rounded-xl shadow-lg border border-primary-200 cursor-pointer"
          >
            <summary class="flex items-center justify-between font-bold text-xl text-gray-900">
              Comment fonctionne l'IA adaptative ?
              <svg
                class="w-6 h-6 transform transition-transform duration-300 group-open:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </summary>
            <p class="mt-4 text-gray-700 text-lg leading-relaxed">
              Nous utilisons l’intelligence artificielle pour corriger vos exercices d’expression
              dans des conditions similaires à celles de l’examen. Elle s’adapte à votre niveau et
              personnalise chaque correction pour optimiser votre progression.
            </p>
          </details>
        </div>
      </div>
    </section>

    <section
      class="relative bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 text-white"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
        <h2 class="text-3xl sm:text-3xl font-bold mb-6">Prêt à parler couramment ?</h2>
        <p class="text-primary-100 mb-10 max-w-3xl mx-auto">
          Rejoignez des milliers d'apprenants et commencez votre voyage vers la maîtrise
          linguistique dès aujourd'hui.
        </p>
        <a
          href="/dashboard"
          class="inline-flex items-center px-12 py-6 bg-white text-primary-600 font-bold rounded-xl hover:bg-primary-50 hover:scale-105 transition-all duration-300 shadow-2xl"
        >
          Commencer l'aventure Linguidia
        </a>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useLanguageStore } from '@/stores/language'
import { useGeneralStore } from '@/stores/general'
import { useAuthStore } from '@/stores/auth.js'
import LoadingState from '@/components/exercices/LoadingState.vue'
import { useHead } from '@vueuse/head'

useHead({
  title: 'Linguidia - Préparation aux tests internationaux',
  meta: [
    {
      name: 'description',
      content:
        'Linguidia est une plateforme innovante dédiée à la préparation aux tests internationaux comme TCF Canada, TOEFL, IELTS, TOEIC et bien plus.',
    },
    { property: 'og:title', content: 'Linguidia - Préparation aux tests internationaux' },
    {
      property: 'og:description',
      content:
        'Entraînez-vous efficacement aux examens TOEFL, IELTS, TOEIC et autres tests internationaux grâce à Linguidia.',
    },
    { property: 'og:type', content: 'website' },
    { property: 'og:image', content: '/logo-long.png' }, // Remplace par ton image réelle
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Linguidia - Préparation aux tests internationaux' },
    {
      name: 'twitter:description',
      content:
        'Préparez-vous aux examens TOEFL, IELTS, TOEIC et autres certifications internationales avec Linguidia.',
    },
    { name: 'twitter:image', content: '/logo-long.png' }, // Idem
  ],
  link: [{ rel: 'canonical', href: 'https://linguidia.com/' }],
})

const languageStore = useLanguageStore()
const generalStore = useGeneralStore()
const authStore = useAuthStore()
const router = useRouter()

const isAuthenticated = computed(() => authStore.isAuthenticated)
const examTypes = computed(() => languageStore.examTypes)

const formatPrice = (price) => {
  if (price === null || price === undefined) return '0,0'
  return new Intl.NumberFormat('fr-CM', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(price)
}

onMounted(async () => {
  await languageStore.fetchExamTypes()
  await generalStore.fetchPackages()
})

const navigateToExamType = (slug) => {
  if (slug) {
    router.push('/dashboard/exercises?examTypeSlug=' + slug)
  }
}

const selectPackage = (pkg) => {
  const button = event.currentTarget
  button.classList.add('animate-pulse')

  setTimeout(() => {
    button.classList.remove('animate-pulse')
  }, 300)

  if (authStore.isAuthenticated) {
    router.push(
      `/buy-package/${pkg?.slug}${generalStore.codePromo ? '/' + generalStore.codePromo : ''}`,
    )
  } else {
    router.push(
      `/login?redirect=/buy-package/${pkg?.slug}${generalStore.codePromo ? '/' + generalStore.codePromo : ''}`,
    )
  }
}
</script>

<style scoped>
details > summary {
  list-style: none;
}
details > summary::-webkit-details-marker {
  display: none;
}
.border-3 {
  border-width: 3px;
}
#header {
  background-image: url('/students2.webp');
  background-repeat: no-repeat;
  background-size: cover;
}

@media screen and (max-width: 500px) {
  #header {
    background-image: url('/students.long.webp');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
  }
}
</style>
